import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Paper
} from '@mui/material';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import AssessmentIcon from '@mui/icons-material/Assessment';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ListAltIcon from '@mui/icons-material/ListAlt';
import BookIcon from '@mui/icons-material/Book';
import DownloadIcon from '@mui/icons-material/Download';
import PeopleIcon from '@mui/icons-material/People';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const Pariksha = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const { user } = useAuth();

  // Check if user has admin privileges
  const hasAdminAccess = user && ['Admin', 'SuperUser', 'PathashalaAdmin'].includes(user.role);
  console.log('User role:', user);

  const sections = [
    {
      path: '/pariksha-syllabus',
      icon: BookIcon,
      title: {
        telugu: 'పరీక్ష ప్రణాళికల విభాగం',
        sanskrit: 'परीक्षा प्रणालिका विभागः'
      },
      description: {
        telugu: 'వటువుల పరీక్ష దరఖాస్తు నమోదు',
        sanskrit: 'वातुवानां परीक्षा आवेदनस्य पञ्जीकरणम्'
      },
      color: '#1976d2',
      requiresAdmin: false // Available to everyone
    },
    {
      path: '/vatuvu',
      icon: PersonAddAlt1Icon,
      title: {
        telugu: 'పరీక్ష దరఖాస్తు విభాగం',
        sanskrit: 'परीक्षा आवेदन विभागः'
      },
      description: {
        telugu: 'వటువుల పరీక్ష దరఖాస్తు నమోదు',
        sanskrit: 'वातुवानां परीक्षा आवेदनस्य पञ्जीकरणम्'
      },
      color: '#1976d2',
      requiresAdmin: false // Available to everyone
    },
    {
      path: '/pariksha-results',
      icon: AssessmentIcon,
      title: {
        telugu: 'పరీక్ష ఫలితముల విభాగం',
        sanskrit: 'परीक्षा फलानां विभागः'
      },
      description: {
        telugu: 'పరీక్ష ఫలితముల నమోదు మరియు నిర్వహణ',
        sanskrit: 'परीक्षा फलानां पञ्जीकरणं च व्यवस्थापनम्'
      },
      color: '#2e7d32',
      requiresAdmin: false // Available to everyone
    },
    {
      path: '/pariksha-adhikaris',
      icon: PeopleIcon,
      title: {
        telugu: 'పరీక్ష అధికారుల వివరములు',
        sanskrit: 'परीक्षा अधिकारिणां विवरणानि'
      },
      description: {
        telugu: 'పరీక్ష అధికారుల వివరములు',
        sanskrit: 'परीक्षा अधिकारिणां विवरणानि'
      },
      color: '#1976d2',
      requiresAdmin: false // Available to everyone
    },
    {
      path: '/parishaadhikari',
      icon: VerifiedUserIcon,
      title: {
        telugu: 'పరీక్షాధికారి విభాగం',
        sanskrit: 'परीक्षाधिकारि विभागः'
      },
      description: {
        telugu: 'పరీక్షాధికారి వీక్షణ మరియు నిర్వహణ',
        sanskrit: 'परीक्षाधिकारिणः दर्शनं च व्यवस्थापनम्'
      },
      color: '#ed6c02',
      requiresAdmin: false // Available to everyone
      //requiresAdmin: true,
      //roleRequired: ['Admin','SuperUser'] // Specific roles required
    },
    {
      path: '/pathasala-exam-status',
      icon: ListAltIcon,
      title: {
        telugu: 'పాఠశాల పరీక్ష నమోదు స్థితి',
        sanskrit: 'पाठशाला परीक्षा पञ्जीकरण स्थितिः'
      },
      description: {
        telugu: 'పాఠశాల వటువుల నమోదు స్థితి వీక్షణ',
        sanskrit: 'पाठशाला वाटुवुनां पञ्जीकरण स्थिति दर्शनम्'
      },
      color: '#9c27b0',
      requiresAdmin: true,
      roleRequired: 'PathashalaAdmin' // Specific role requirement
    },
    {
      path: '/download-patta',
      icon: DownloadIcon,
      title: {
        telugu: 'మీ పట్టా డౌన్లోడ్ చేసుకోండి',
        sanskrit: 'तव पत्रं डाउनलोड् कुरुत'
      },
      description: {
        telugu: 'మీ పట్టా డౌన్లోడ్ చేసుకోండి',
        sanskrit: 'तव पत्रं डाउनलोड् कुरुत'
      },
      color: '#b02742f3'
    }
  ];

  // Filter sections based on user access
  const visibleSections = sections.filter(section => {
    if (!section.requiresAdmin) return true; // Available to all
    if (!hasAdminAccess) return false; // Requires admin but user doesn't have access
    if (section.roleRequired) {
      // Specific role required
      return user && user.role === section.roleRequired;
    }
    return true; // Admin access and no specific role required
  });

  return (
    <Container maxWidth="lg">
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 4, 
          background: 'linear-gradient(135deg, #7f4b2d 0%, #d0b996 100%)',
          borderRadius: 5
        }}
      >
        <Typography 
          variant="h4" 
          sx={{ 
            color: '#fff', 
            fontWeight: 700,
            textAlign: 'center',
            mb: 1
          }}
        >
          {lang === 'telugu' ? 'పరీక్ష విభాగం' : 'परीक्षा विभागः'}
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            color: 'rgba(255,255,255,0.9)', 
            textAlign: 'center'
          }}
        >
          {lang === 'telugu' 
            ? 'పరీక్షల నిర్వహణ కోసం కింది విభాగాలను ఎంచుకోండి' 
            : 'परीक्षाणां व्यवस्थापनाय अधः विभागान् चिनुत'}
        </Typography>
      </Paper>

      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: 3,
        justifyContent: 'flex-start'
      }}>
        {visibleSections.map((section) => {
          const Icon = section.icon;
          return (
            <Card 
              key={section.path}
              elevation={2}
              sx={{ 
                width: { xs: '100%', sm: 'calc(37.5% - 12px)', md: 'calc(25% - 18px)' },
                height: 240,
                display: 'flex',
                borderRadius: 10,
                flexDirection: 'column',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                }
              }}
            >
                <CardActionArea 
                  onClick={() => navigate(section.path)}
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    p: 3 
                  }}
                >
                  <CardContent sx={{ 
                    textAlign: 'center',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        p: 2,
                        borderRadius: '50%',
                        bgcolor: `${section.color}15`,
                        mb: 2,
                        mx: 'auto'
                      }}
                    >
                      <Icon 
                        sx={{ 
                          fontSize: 48, 
                          color: section.color
                        }} 
                      />
                    </Box>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600,
                        color: '#7f4b2d',
                        mb: 1,
                        minHeight: 56,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word'
                      }}
                    >
                      {section.title[lang]}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#666',
                        lineHeight: 1.6,
                        minHeight: 48,
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word'
                      }}
                    >
                      {section.description[lang]}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
          );
        })}
      </Box>

      <Box sx={{ mt: 4, p: 3, bgcolor: '#fffaf1', borderRadius: 2 }}>
        <Typography variant="body2" sx={{ color: '#666', textAlign: 'center' }}>
          {lang === 'telugu'
            ? 'మీ అవసరమైన విభాగాన్ని ఎంచుకోండి. ప్రతి విభాగం నిర్దిష్ట పరీక్ష నిర్వహణ కార్యకలాపాలను అందిస్తుంది.'
            : 'आवश्यकं विभागं चिनुत। प्रत्येकं विभागः विशिष्टं परीक्षा व्यवस्थापन कार्याणि प्रदास्यति।'}
        </Typography>
      </Box>
    </Container>
  );
};

export default Pariksha;
